import { useMemo, useState } from 'react';
import type { ConflictEdge, DesignConfig } from '../api/types';
import {
  axisTicks,
  barLeftPx,
  barWidthPx,
  barsOverlapInRange,
  defaultVisibleRange,
  formatBarRange,
  nowLineLeftPx,
  pixelsPerDay,
  priorityPalette,
  type ZoomLevel,
} from '../lib/timelineMath';
import { ConfigBarPopover } from './ConfigBarPopover';

interface Props {
  configs: DesignConfig[];
  conflictEdges: ConflictEdge[];
  storeId: string;
  onConfigSaved: () => void;
}

const ROW_HEIGHT = 44;
const ROW_GAP = 8;
const BAR_HEIGHT = 32;
const AXIS_HEIGHT = 38;
const CHART_PAD_Y = 14;

function isAlwaysOn(c: DesignConfig): boolean {
  return !c.active_from && !c.active_to;
}

export function Timeline({ configs, conflictEdges, storeId, onConfigSaved }: Props) {
  const [zoom, setZoom] = useState<ZoomLevel>('month');
  const [selected, setSelected] = useState<DesignConfig | null>(null);
  const [hoveredEdge, setHoveredEdge] = useState<string | null>(null);

  const range = useMemo(() => defaultVisibleRange(), []);
  const ppd = pixelsPerDay(zoom);
  const totalDays = useMemo(
    () => (range.end.getTime() - range.start.getTime()) / (1000 * 60 * 60 * 24),
    [range],
  );
  const chartWidth = totalDays * ppd;

  const sortedConfigs = useMemo(
    () => [...configs].sort((a, b) => b.priority - a.priority || a.name.localeCompare(b.name)),
    [configs],
  );

  const ticks = useMemo(() => axisTicks(range.start, range.end, zoom), [range, zoom]);
  const nowLine = nowLineLeftPx(new Date(), range.start, zoom);
  const chartHeight = CHART_PAD_Y * 2 + sortedConfigs.length * (ROW_HEIGHT + ROW_GAP);

  // Pre-compute bar geometry per config so axis, conflict edges and bars stay in sync.
  const barGeo = useMemo(() => {
    return sortedConfigs.map((c, i) => ({
      config: c,
      idx: i,
      left: barLeftPx(c.active_from ? new Date(c.active_from) : null, range.start, zoom),
      width: barWidthPx(
        c.active_from ? new Date(c.active_from) : null,
        c.active_to ? new Date(c.active_to) : null,
        range.start,
        range.end,
        zoom,
      ),
      top: CHART_PAD_Y + i * (ROW_HEIGHT + ROW_GAP) + (ROW_HEIGHT - BAR_HEIGHT) / 2,
      always: isAlwaysOn(c),
    }));
  }, [sortedConfigs, range, zoom]);

  // Filter edges to the visible window + lookup geo by name.
  const visibleEdges = useMemo(() => {
    return conflictEdges
      .map((edge) => {
        const winner = barGeo.find((g) => g.config.name === edge.winner);
        const loser = barGeo.find((g) => g.config.name === edge.loser);
        if (!winner || !loser) return null;
        if (
          !barsOverlapInRange(
            { active_from: winner.config.active_from ?? null, active_to: winner.config.active_to ?? null },
            { active_from: loser.config.active_from ?? null, active_to: loser.config.active_to ?? null },
            range.start,
            range.end,
          )
        )
          return null;
        // Overlap region (in px) between winner and loser windows
        const overlapLeft = Math.max(winner.left, loser.left);
        const overlapRight = Math.min(winner.left + winner.width, loser.left + loser.width);
        const overlapWidth = Math.max(8, overlapRight - overlapLeft);
        return { edge, winner, loser, overlapLeft, overlapWidth };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [conflictEdges, barGeo, range]);

  const hasConfigs = sortedConfigs.length > 0;

  return (
    <div className="ff-card overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-ff-sand/60 bg-ff-paper/40">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widish text-ff-mute">
            Schedule
          </p>
          <h2 className="font-display text-xl text-ff-forest leading-tight">
            All campaigns
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {visibleEdges.length > 0 && (
            <span
              className="ff-chip bg-ff-accent-soft text-ff-accent"
              data-testid="timeline-conflict-count"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-ff-accent" />
              {visibleEdges.length} conflict{visibleEdges.length === 1 ? '' : 's'}
            </span>
          )}
          <div
            role="radiogroup"
            aria-label="Zoom"
            className="grid grid-cols-3 gap-0.5 rounded-full border border-ff-sand/60 bg-white/60 p-0.5"
          >
            {(['month', 'quarter', 'year'] as ZoomLevel[]).map((z) => {
              const active = z === zoom;
              return (
                <button
                  key={z}
                  role="radio"
                  aria-checked={active}
                  onClick={() => setZoom(z)}
                  className={`text-[11px] font-mono uppercase tracking-widish px-3 py-1 rounded-full transition-colors ${
                    active
                      ? 'bg-ff-forest text-ff-ivory shadow-soft'
                      : 'text-ff-mute hover:text-ff-forest'
                  }`}
                  data-testid={`zoom-${z}`}
                >
                  {z.charAt(0).toUpperCase() + z.slice(1)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {!hasConfigs && (
        <div className="px-8 py-16 text-center">
          <p className="font-display text-2xl text-ff-forest">No configs to show.</p>
          <p className="mt-2 text-sm text-ff-mute">
            Designs appear here once you create them in your catalog workspace.
          </p>
        </div>
      )}

      {hasConfigs && (
        <div className="overflow-x-auto" data-testid="timeline-scroll">
          <div
            className="relative"
            style={{ width: chartWidth, minHeight: AXIS_HEIGHT + chartHeight }}
          >
            {/* Axis */}
            <div
              className="relative border-b border-ff-sand/60"
              style={{ height: AXIS_HEIGHT }}
              aria-hidden="true"
            >
              {ticks.map((t, i) => (
                <div
                  key={`tick-${i}`}
                  className={`absolute top-0 ${t.major ? '' : 'opacity-60'}`}
                  style={{ left: t.leftPx }}
                >
                  <span
                    className={`absolute top-0 w-px ${
                      t.major ? 'bg-ff-sand h-full' : 'bg-ff-sand/50 h-3'
                    }`}
                  />
                  <span
                    className={`absolute top-[6px] left-1.5 whitespace-nowrap ${
                      t.major
                        ? 'font-mono text-[10px] uppercase tracking-widish text-ff-forest'
                        : 'font-mono text-[9px] text-ff-mute'
                    }`}
                  >
                    {t.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Chart body */}
            <div className="relative" style={{ height: chartHeight }}>
              {/* Row backgrounds for legibility */}
              {barGeo.map((g) => (
                <div
                  key={`row-${g.config.name}`}
                  className={`absolute left-0 right-0 ${
                    g.idx % 2 === 0 ? 'bg-transparent' : 'bg-ff-paper/40'
                  }`}
                  style={{
                    top: CHART_PAD_Y + g.idx * (ROW_HEIGHT + ROW_GAP),
                    height: ROW_HEIGHT,
                    width: chartWidth,
                  }}
                />
              ))}

              {/* Vertical guide lines aligned to major ticks */}
              {ticks
                .filter((t) => t.major)
                .map((t, i) => (
                  <div
                    key={`vg-${i}`}
                    className="absolute top-0 bottom-0 w-px bg-ff-sand/40"
                    style={{ left: t.leftPx }}
                    aria-hidden="true"
                  />
                ))}

              {/* Now line */}
              <div
                className="absolute top-0 bottom-0 z-10 pointer-events-none"
                style={{ left: nowLine }}
              >
                <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-ff-accent" />
                <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[3px] bg-ff-accent/20 blur-[2px]" />
                <div className="absolute -top-1 left-1/2 -translate-x-1/2">
                  <span className="font-mono text-[9px] uppercase tracking-widish px-1.5 py-0.5 rounded-full bg-ff-accent text-ff-ivory whitespace-nowrap">
                    Today
                  </span>
                </div>
              </div>

              {/* Conflict bands (drawn behind bars, between winner and loser rows) */}
              {visibleEdges.map(({ edge, winner, loser, overlapLeft, overlapWidth }, i) => {
                const top = Math.min(winner.top, loser.top) + BAR_HEIGHT;
                const height = Math.abs(loser.top - winner.top);
                const isHovered = hoveredEdge === `${edge.winner}-${edge.loser}`;
                return (
                  <div
                    key={`edge-${edge.winner}-${edge.loser}-${i}`}
                    className={`absolute pointer-events-none transition-opacity ${
                      isHovered ? 'opacity-100' : 'opacity-70'
                    }`}
                    style={{ left: overlapLeft, top, width: overlapWidth, height }}
                  >
                    <div className="absolute inset-x-0 inset-y-0 border-l border-r border-dashed border-ff-accent/45" />
                    <div className="absolute left-0 right-0 top-1/2 h-px bg-ff-accent/30" />
                  </div>
                );
              })}

              {/* Bars */}
              {barGeo.map((g) => {
                const palette = priorityPalette(g.config.priority, g.always);
                const overlapping = visibleEdges
                  .filter((e) => e.edge.loser === g.config.name)
                  .map((e) => e.edge);
                const isLoser = overlapping.length > 0;
                const winnerNames = overlapping.map((e) => e.winner);
                return (
                  <button
                    key={g.config.name}
                    type="button"
                    onClick={() => setSelected(g.config)}
                    onMouseEnter={() => {
                      if (isLoser) {
                        setHoveredEdge(`${winnerNames[0]}-${g.config.name}`);
                      }
                    }}
                    onMouseLeave={() => setHoveredEdge(null)}
                    className={`group absolute rounded-md ${palette.fill} ${palette.ring} ${palette.text} ${
                      g.always ? 'border border-dashed border-ff-forest/30' : ''
                    } hover:shadow-hover transition-shadow text-left overflow-hidden`}
                    style={{ top: g.top, left: g.left, width: g.width, height: BAR_HEIGHT }}
                    data-testid={`timeline-bar-${g.config.name}`}
                    title={`${g.config.name} · ${formatBarRange(g.config.active_from, g.config.active_to)} · priority ${g.config.priority}`}
                  >
                    <span
                      className={`absolute inset-y-0 left-0 grid place-items-center px-2 font-mono text-[10px] uppercase tracking-widish ${palette.badgeFill} ${palette.badgeText} border-r border-ff-ivory/10`}
                    >
                      P{g.config.priority}
                    </span>
                    <span className="absolute inset-y-0 left-9 right-2 flex items-center gap-2 min-w-0">
                      <span className="font-medium text-[12.5px] truncate">
                        {g.config.name}
                      </span>
                      {isLoser && g.width > 110 && (
                        <span className="ff-chip bg-ff-accent/15 text-ff-accent font-mono normal-case text-[9px] py-0 px-1.5 shrink-0">
                          hidden
                        </span>
                      )}
                      {g.always && g.width > 130 && (
                        <span className="font-mono text-[9px] uppercase tracking-widish opacity-75 truncate">
                          always on
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      {hasConfigs && (
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-t border-ff-sand/60 bg-ff-paper/40 text-[10px] font-mono uppercase tracking-widish text-ff-mute">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-3.5 rounded bg-ff-forest" />
              high priority
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-3.5 rounded bg-ff-sand" />
              low priority
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-3.5 rounded bg-ff-mint-deep/70 border border-dashed border-ff-forest/30" />
              always on
            </span>
            {visibleEdges.length > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-3.5 border-l border-r border-dashed border-ff-accent/60" />
                conflict band
              </span>
            )}
          </div>
          <span>
            Drag the chart sideways · click a bar to edit its window
          </span>
        </div>
      )}

      {selected && (
        <ConfigBarPopover
          config={selected}
          storeId={storeId}
          conflicts={conflictEdges.filter(
            (e) => e.winner === selected.name || e.loser === selected.name,
          )}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            onConfigSaved();
          }}
        />
      )}
    </div>
  );
}
